// swarm-share — turn any app's "share / export" button into a Swarm content-addressed link.
//
// Zero dependencies. Works in browsers and Node 18+ (uses global `fetch` and WebCrypto).
// The whole point: replace a centralized share backend (Firebase, a pastebin server, a gist)
// with plain HTTP against a public Swarm gateway. This is the ~30-line core distilled from the
// Strudel -> Swarm port, so adopting it in a new app is a 3-line change.
//
//   import { shareText, importText } from 'swarm-share';
//   const { url } = await shareText(code);          // upload -> content-addressed link
//   const code   = await importText(urlOrHash);     // fetch it back
//
// Persistence note: the default public gateway (bzz.limo) pays the postage stamp for you, so
// uploads are free and anonymous — but content lives only as long as that stamp. For durable
// production data, pass your own gateway and/or a `swarm-postage-batch-id` header.

const DEFAULT_GATEWAY = 'https://bzz.limo';

/** POST raw content to a Swarm gateway. Returns the content-addressed reference (hex hash). */
export async function swarmUpload(body, { gateway = DEFAULT_GATEWAY, contentType = 'text/plain', headers = {} } = {}) {
  const res = await fetch(`${gateway}/bzz`, {
    method: 'POST',
    headers: { 'Content-Type': contentType, ...headers },
    body,
  });
  if (!res.ok) throw new Error(`Swarm upload failed: ${res.status} ${res.statusText}`);
  const data = await res.json().catch(() => ({}));
  if (!data.reference) throw new Error('Swarm upload returned no reference');
  return data.reference;
}

/** GET content from a Swarm gateway. Accepts a bare hash OR a full bzz URL. Returns the Response. */
export async function swarmDownload(hashOrUrl, { gateway = DEFAULT_GATEWAY } = {}) {
  const ref = extractReference(hashOrUrl);
  const res = await fetch(`${gateway}/bzz/${ref}/`);
  if (!res.ok) throw new Error(`Swarm download failed: ${res.status} ${res.statusText}`);
  return res;
}

/** Pull a Swarm reference out of a hash or any bzz URL (e.g. https://bzz.limo/bzz/<ref>/). */
export function extractReference(hashOrUrl) {
  const s = String(hashOrUrl).trim();
  const m = s.match(/(?:bzz\/)?([a-fA-F0-9]{64,128})/); // 64 hex = unencrypted, 128 = encrypted ref
  return m ? m[1] : s;
}

/** Build the canonical gateway URL for a reference. */
export function swarmUrl(reference, { gateway = DEFAULT_GATEWAY } = {}) {
  return `${gateway}/bzz/${reference}/`;
}

// --- Convenience: text & JSON ------------------------------------------------

/** Upload text. Returns { reference, url }. */
export async function shareText(text, opts = {}) {
  const reference = await swarmUpload(text, { contentType: 'text/plain', ...opts });
  return { reference, url: swarmUrl(reference, opts) };
}

/** Download text from a hash or URL. */
export async function importText(hashOrUrl, opts = {}) {
  return (await swarmDownload(hashOrUrl, opts)).text();
}

/** Upload a JSON-serializable value. Returns { reference, url }. */
export async function shareJSON(value, opts = {}) {
  const reference = await swarmUpload(JSON.stringify(value), { contentType: 'application/json', ...opts });
  return { reference, url: swarmUrl(reference, opts) };
}

/** Download and parse JSON from a hash or URL. */
export async function importJSON(hashOrUrl, opts = {}) {
  return (await swarmDownload(hashOrUrl, opts)).json();
}

// --- Deep-link helper (the Strudel `?import=<hash>` pattern) ------------------

/**
 * If the current page URL has ?<param>=<hash>, fetch it from Swarm, strip the param from the
 * address bar, and return the text. Returns null if the param is absent. Browser-only.
 */
export async function importFromUrlParam(param = 'import', opts = {}) {
  if (typeof globalThis.location === 'undefined') return null;
  const url = new URL(globalThis.location.href);
  const ref = url.searchParams.get(param);
  if (!ref) return null;
  const text = await importText(ref, opts);
  url.searchParams.delete(param);
  globalThis.history?.replaceState?.(null, '', url.toString());
  return text;
}

// --- Optional client-side encryption (Excalidraw pattern) --------------------
// The key lives in the URL fragment (#key) and is NEVER sent to the gateway — Swarm stores only
// ciphertext. This gives private shares with no backend and no accounts.

const b64u = {
  enc: (bytes) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
  dec: (s) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0)),
};

/** Encrypt text (AES-GCM-256), upload ciphertext, return { reference, key, url } with #key in url. */
export async function shareEncrypted(text, opts = {}) {
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(text)));
  const blob = new Uint8Array(iv.length + ct.length);
  blob.set(iv);
  blob.set(ct, iv.length);
  const reference = await swarmUpload(blob, { contentType: 'application/octet-stream', ...opts });
  const rawKey = b64u.enc(new Uint8Array(await crypto.subtle.exportKey('raw', key)));
  return { reference, key: rawKey, url: `${swarmUrl(reference, opts)}#${rawKey}` };
}

/** Download + decrypt. `keyB64` defaults to the location fragment (#key) when omitted. */
export async function importEncrypted(hashOrUrl, keyB64, opts = {}) {
  if (!keyB64 && typeof globalThis.location !== 'undefined') keyB64 = globalThis.location.hash.slice(1);
  if (!keyB64) throw new Error('Decryption key missing (expected in URL fragment or argument)');
  const buf = new Uint8Array(await (await swarmDownload(hashOrUrl, opts)).arrayBuffer());
  const iv = buf.slice(0, 12);
  const ct = buf.slice(12);
  const key = await crypto.subtle.importKey('raw', b64u.dec(keyB64), { name: 'AES-GCM' }, false, ['decrypt']);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return new TextDecoder().decode(pt);
}

export default { swarmUpload, swarmDownload, extractReference, swarmUrl, shareText, importText, shareJSON, importJSON, importFromUrlParam, shareEncrypted, importEncrypted };
